import { ChangeEvent, KeyboardEvent, useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { Tooltip } from '@mui/material'
import SendIcon from '@mui/icons-material/Send'
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd'
import CheckIcon from '@mui/icons-material/Check'
import RefreshIcon from '@mui/icons-material/Refresh'
import CloseIcon from '@mui/icons-material/Close'
import { PageArea } from '..'
import { Text, Title } from '../../components/typography'
import { colorPalette } from '../../style/colorPalette'
import AttachmentIcon from '../../components/icons/AttachmentIcon'
import { AiAgentMessageList } from '../../features/AiAgentChat/AiAgentMessageList'
import { QueuedMessageList } from '../../features/AiAgentChat/QueuedMessageList'
import { useAiAgentConversation } from '../../features/AiAgentChat/hooks/useAiAgentConversation'
import {
  releaseImageAttachments,
  toUploadedImageRefs,
  useAiAgentImageUpload,
} from '../../features/AiAgentChat/hooks/useAiAgentImageUpload'
import { useAiAgentMessageQueue } from '../../features/AiAgentChat/hooks/useAiAgentMessageQueue'
import {
  AiAgentChatDraft,
  mergeDrafts,
} from '../../features/AiAgentChat/queuedMessages'
import {
  AttachmentPreviewItem,
  AttachmentsPreviewRow,
  AttachmentThumb,
  AttachmentUploadingOverlay,
  ChatCard,
  ChatColumn,
  ChatHeader,
  ChatInput,
  HeaderIconButton,
  HeaderTexts,
  InlineError,
  InputArea,
  RemoveAttachmentButton,
  SendButton,
} from './styles'

export const AiAgentChatPage = () => {
  const intl = useIntl()
  const [inputValue, setInputValue] = useState('')
  // 送信待ちを破棄したことの通知。会話自体のエラーとは別の事情なので独立して持つ。
  const [queueNotice, setQueueNotice] = useState<string | undefined>(undefined)
  const {
    messages,
    isStreaming,
    inlineError,
    lastTurnResult,
    sendMessage,
    resetConversation,
  } = useAiAgentConversation({
    greetingText: intl.formatMessage({ id: 'aiAgentChat.greeting' }),
    busyErrorText: intl.formatMessage({ id: 'aiAgentChat.error.busy' }),
    generalErrorText: intl.formatMessage({ id: 'aiAgentChat.error.general' }),
    streamErrorText: intl.formatMessage({
      id: 'aiAgentChat.error.streaming',
    }),
  })
  const {
    attachments,
    errorMessage: attachErrorMessage,
    isUploading,
    addFiles,
    removeAttachment,
    clearAttachments,
    detachAttachments,
    restoreAttachments,
  } = useAiAgentImageUpload()
  const {
    queuedMessages,
    editingMessageId,
    enqueue,
    removeQueuedMessage,
    startEditing,
    commitEditing,
    cancelEditing,
    takeQueuedMessagesForSend,
    discardQueue,
  } = useAiAgentMessageQueue()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const chatInputRef = useRef<HTMLTextAreaElement>(null)

  // チャット専用ページはWBSアイテムを添付する導線を持たないため、下書きは本文と画像だけで構成する。
  const buildCurrentDraft = (): AiAgentChatDraft => ({
    text: inputValue,
    images: attachments,
    wbsItems: [],
  })

  // 送信待ちから戻ってきた内容を入力欄へ載せる。
  const applyDraft = (draft: AiAgentChatDraft) => {
    setInputValue(draft.text)
    restoreAttachments(draft.images)
    chatInputRef.current?.focus()
  }

  const sendDraft = async (draft: AiAgentChatDraft) => {
    const imageRefs = toUploadedImageRefs(draft.images)
    // チャット専用ページには周辺文脈（プロジェクト・WBS等）が存在しないため screen のみ送る。
    const result = await sendMessage(
      draft.text,
      { screen: 'chat' },
      imageRefs.length > 0 ? imageRefs : undefined
    )
    if (result === 'busy') {
      // 応答中で受け付けられなかったため、送信待ちへ戻して次のターン終了時に送る。
      enqueue(draft)
      return
    }
    // 送信済みバブルはアップロード済みURLを表示するため、プレビューは解放してよい。
    releaseImageAttachments(draft.images)
  }

  // 応答が終わったあとに編集を確定・取消した場合、送信の契機がここにしかないため送り出す。
  const flushQueuedMessages = () => {
    if (isStreaming) {
      return
    }
    const queued = takeQueuedMessagesForSend()
    if (queued) {
      void sendDraft(queued)
    }
  }

  // 入力欄の確定操作。AI応答中は送信せず送信待ちに積み、編集中はその内容を確定する。
  const handleSubmit = () => {
    if (isUploading) {
      return
    }
    setQueueNotice(undefined)
    if (editingMessageId) {
      applyDraft(commitEditing(buildCurrentDraft()))
      flushQueuedMessages()
      return
    }
    if (inputValue.trim() === '') {
      return
    }
    const draft: AiAgentChatDraft = {
      text: inputValue.trim(),
      images: detachAttachments(),
      wbsItems: [],
    }
    setInputValue('')
    if (isStreaming) {
      enqueue(draft)
    } else {
      // 応答完了後に編集を確定した場合など、送信待ちが残っていればまとめて送る。
      const queued = takeQueuedMessagesForSend()
      void sendDraft(queued ? mergeDrafts([queued, draft]) : draft)
    }
    // 送信ボタンから確定した場合もそのまま入力を続けられるようにする。
    chatInputRef.current?.focus()
  }

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value)
  }

  const handleAttachClick = () => {
    fileInputRef.current?.click()
  }

  const handleFileInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      addFiles(Array.from(event.target.files))
    }
    event.target.value = ''
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape' && editingMessageId) {
      event.preventDefault()
      applyDraft(cancelEditing(buildCurrentDraft()))
      flushQueuedMessages()
      return
    }
    if (
      event.key === 'Enter' &&
      !event.shiftKey &&
      !event.nativeEvent.isComposing
    ) {
      event.preventDefault()
      handleSubmit()
    }
  }

  const handleEditQueuedMessage = (id: string) => {
    // アップロード中の添付を入力欄から退避すると完了通知の宛先が失われ、以後送信できなくなる。
    if (isUploading) {
      return
    }
    const restored = startEditing(id, buildCurrentDraft())
    if (restored) {
      applyDraft(restored)
    }
  }

  const handleRefresh = () => {
    resetConversation()
    setInputValue('')
    clearAttachments()
    discardQueue()
    setQueueNotice(undefined)
  }

  // ターン終了イベントを1度だけ拾うための連番。
  const handledTurnSeqRef = useRef(0)
  useEffect(() => {
    if (!lastTurnResult || lastTurnResult.seq <= handledTurnSeqRef.current) {
      return
    }
    handledTurnSeqRef.current = lastTurnResult.seq
    if (lastTurnResult.status === 'error') {
      // 失敗したターンの後続を自動送信しても同じ失敗を繰り返すため、送信待ちは破棄する。
      if (discardQueue() > 0) {
        setQueueNotice(
          intl.formatMessage({ id: 'aiAgentChat.queue.discarded' })
        )
      }
      return
    }
    const merged = takeQueuedMessagesForSend()
    if (merged) {
      void sendDraft(merged)
    }
    // 他の依存で再実行すると、送信待ちを取り出した直後の状態でもう一度動いてしまうため、
    // ターンの終了イベントにのみ反応させる。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastTurnResult])

  const submitTooltipId = editingMessageId
    ? 'aiAgentChat.queue.commitEdit'
    : isStreaming
      ? 'aiAgentChat.queue.add'
      : 'aiAgentChat.send'
  const inputPlaceholder = intl.formatMessage({
    id: editingMessageId
      ? 'aiAgentChat.queue.editPlaceholder'
      : 'aiAgentChat.input.placeholder',
  })

  const renderSubmitIcon = () => {
    if (editingMessageId) return <CheckIcon />
    if (isStreaming) return <PlaylistAddIcon />
    return <SendIcon />
  }

  return (
    <PageArea>
      <ChatColumn>
        <ChatCard>
          <ChatHeader>
            <HeaderTexts>
              <Title variant="l">
                {intl.formatMessage({ id: 'aiAgentChat.title' })}
              </Title>
              <Text variant="2s" color={colorPalette.monotone[5]}>
                {intl.formatMessage({ id: 'aiAgentChat.description' })}
              </Text>
            </HeaderTexts>
            <Tooltip title={intl.formatMessage({ id: 'aiAgentChat.reset' })}>
              <span>
                <HeaderIconButton size="small" onClick={handleRefresh}>
                  <RefreshIcon fontSize="small" />
                </HeaderIconButton>
              </span>
            </Tooltip>
          </ChatHeader>
          <AiAgentMessageList messages={messages} isStreaming={isStreaming} />
          {inlineError && <InlineError>{inlineError}</InlineError>}
          {attachErrorMessage && (
            <InlineError>{attachErrorMessage}</InlineError>
          )}
          {queueNotice && <InlineError>{queueNotice}</InlineError>}
          <QueuedMessageList
            messages={queuedMessages}
            editingMessageId={editingMessageId}
            // チャット専用ページはWBSアイテムを添付する導線を持たない。
            editingAttachmentCounts={{
              images: attachments.length,
              wbsItems: 0,
            }}
            disableEdit={isUploading}
            onEdit={handleEditQueuedMessage}
            onRemove={removeQueuedMessage}
          />
          {attachments.length > 0 && (
            <AttachmentsPreviewRow>
              {attachments.map(attachment => (
                <AttachmentPreviewItem
                  key={attachment.id}
                  hasError={attachment.status === 'error'}
                >
                  <AttachmentThumb src={attachment.previewUrl} alt="" />
                  {attachment.status === 'uploading' && (
                    <AttachmentUploadingOverlay />
                  )}
                  <RemoveAttachmentButton
                    size="small"
                    onClick={() => removeAttachment(attachment.id)}
                    aria-label={intl.formatMessage({
                      id: 'aiAgentChat.attach.remove',
                    })}
                  >
                    <CloseIcon fontSize="inherit" />
                  </RemoveAttachmentButton>
                </AttachmentPreviewItem>
              ))}
            </AttachmentsPreviewRow>
          )}
          <InputArea>
            <Tooltip
              title={intl.formatMessage({ id: 'aiAgentChat.attach.button' })}
            >
              <span>
                <HeaderIconButton size="small" onClick={handleAttachClick}>
                  <AttachmentIcon fontSize="small" />
                </HeaderIconButton>
              </span>
            </Tooltip>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple={true}
              hidden={true}
              onChange={handleFileInputChange}
            />
            <ChatInput
              placeholder={inputPlaceholder}
              fullWidth={true}
              multiline={true}
              maxRows={6}
              inputProps={{ 'aria-label': inputPlaceholder }}
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              inputRef={chatInputRef}
            />
            <Tooltip title={intl.formatMessage({ id: submitTooltipId })}>
              <span>
                <SendButton
                  onClick={handleSubmit}
                  disabled={
                    isUploading ||
                    (!editingMessageId && inputValue.trim() === '')
                  }
                >
                  {renderSubmitIcon()}
                </SendButton>
              </span>
            </Tooltip>
          </InputArea>
        </ChatCard>
      </ChatColumn>
    </PageArea>
  )
}
